import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { parseISO, startOfDay, subDays } from 'date-fns';
import { supabaseQueryWithRetry } from '@/lib/networkRetry';
import { QUERY_CONFIG } from '@/config/constants';
import { QUERY_KEYS } from '@/config/queries';
import { useMemo } from 'react';
import { getLocalIsoDate } from '@/lib/dateUtils';

export type TimeRange = '7d' | '30d' | '90d';

export type HistoricalMetricKey = 'steps' | 'sleep' | 'active' | 'hrv' | 'resting_hr' | 'calories';

export type HistoricalMetricPoint = {
  date: string;
  steps: number | null;
  sleep: number | null;
  active: number | null;
  hrv: number | null;
  resting_hr: number | null;
  calories: number | null;
};

export type MetricCoverage = {
  availableDays: number;
  totalDays: number;
  missingDates: string[];
};

export type HistoricalMetricCoverage = Record<HistoricalMetricKey, MetricCoverage>;

const HISTORICAL_METRIC_KEYS: HistoricalMetricKey[] = [
  'steps',
  'sleep',
  'active',
  'hrv',
  'resting_hr',
  'calories',
];

const toNumberOrNull = (value: unknown): number | null => {
  if (value == null || value === '') return null;

  const numericValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const toCountValue = (value: unknown): number | null => {
  const numericValue = toNumberOrNull(value);
  return numericValue !== null && numericValue >= 0 ? numericValue : null;
};

const toSleepValue = (value: unknown): number | null => {
  const numericValue = toNumberOrNull(value);
  return numericValue && numericValue > 0 ? numericValue : null;
};

const toHrvValue = (value: unknown): number | null => {
  const numericValue = toNumberOrNull(value);
  return numericValue && numericValue > 0 ? numericValue : null;
};

const buildDateRange = (totalDays: number, today = new Date()) =>
  Array.from({ length: totalDays }, (_, index) =>
    getLocalIsoDate(subDays(today, totalDays - index - 1)),
  );

const normalizeMetricDateKey = (value: string | null | undefined) => {
  if (!value) return null;

  const parsedDate = parseISO(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return getLocalIsoDate(parsedDate);
};

const buildMetricRowsMap = (
  metricsRows:
    | {
        metric_date: string | null;
        steps: unknown;
        sleep_hours: unknown;
        active_minutes: unknown;
        hrv_rmssd: unknown;
        heart_rate: unknown;
        calories: unknown;
      }[]
    | null
    | undefined,
  validDates: Set<string>,
) => {
  const metricsByDate = new Map<string, HistoricalMetricPoint>();

  metricsRows?.forEach((row) => {
    const dateKey = normalizeMetricDateKey(row.metric_date ?? undefined);
    if (!dateKey || !validDates.has(dateKey)) return;

    metricsByDate.set(dateKey, {
      date: dateKey,
      steps: toCountValue(row.steps),
      sleep: toSleepValue(row.sleep_hours),
      active: toCountValue(row.active_minutes),
      hrv: toHrvValue(row.hrv_rmssd),
      resting_hr: toHrvValue(row.heart_rate),
      calories: toCountValue(row.calories),
    });
  });

  return metricsByDate;
};

const mergeMetricPoint = (
  date: string,
  primary?: HistoricalMetricPoint,
): HistoricalMetricPoint => ({
  date,
  steps: primary?.steps ?? null,
  sleep: primary?.sleep ?? null,
  active: primary?.active ?? null,
  hrv: primary?.hrv ?? null,
  resting_hr: primary?.resting_hr ?? null,
  calories: primary?.calories ?? null,
});

export const buildMetricCoverage = (points: HistoricalMetricPoint[]): HistoricalMetricCoverage => {
  const coverage = {} as HistoricalMetricCoverage;

  HISTORICAL_METRIC_KEYS.forEach((metricKey) => {
    const missingDates = points
      .filter((point) => point[metricKey] === null)
      .map((point) => point.date);

    coverage[metricKey] = {
      availableDays: points.length - missingDates.length,
      totalDays: points.length,
      missingDates,
    };
  });

  return coverage;
};

export const useHistoricalData = (userId: string, timeRange: TimeRange = '7d') => {
  const totalDays = useMemo(
    () => (timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90),
    [timeRange],
  );
  const totalComparisonDays = useMemo(() => totalDays * 2, [totalDays]);

  const metricsQuery = useQuery({
    queryKey: QUERY_KEYS.HISTORICAL_METRICS(userId, timeRange),
    queryFn: async () => {
      const today = new Date();
      const dateRange = buildDateRange(totalComparisonDays, today);
      const validDates = new Set(dateRange);
      const startDateIso = dateRange[0];
      const endDateIso = dateRange[dateRange.length - 1];

      const { data: metricsRows } = await supabaseQueryWithRetry(() =>
        supabase
          .from('health_metrics')
          .select('metric_date, steps, sleep_hours, active_minutes, hrv_rmssd, heart_rate, calories')
          .eq('user_id', userId)
          .gte('metric_date', startDateIso)
          .lte('metric_date', endDateIso)
          .order('metric_date', { ascending: true }),
      );

      const metricsByDate = buildMetricRowsMap(metricsRows, validDates);
      return dateRange.map((dateKey) => mergeMetricPoint(dateKey, metricsByDate.get(dateKey)));
    },
    enabled: !!userId,
    staleTime: QUERY_CONFIG.STALE_TIME.LONG, // 5 minutes
    gcTime: QUERY_CONFIG.GC_TIME.MEDIUM, // 10 minutes cache
  });

  const metricsTimeline = useMemo(() => metricsQuery.data || [], [metricsQuery.data]);
  const metricsLoading = useMemo(() => metricsQuery.isLoading, [metricsQuery.isLoading]);

  const { data: pointsData = [], isLoading: pointsLoading } = useQuery({
    queryKey: QUERY_KEYS.HISTORICAL_POINTS(userId, timeRange),
    queryFn: async () => {
      const today = new Date();
      const dateRange = buildDateRange(totalComparisonDays, today);
      const validDates = new Set(dateRange);
      const startDate = startOfDay(subDays(today, totalComparisonDays - 1));

      const { data } = await supabaseQueryWithRetry(() =>
        supabase
          .from('points_ledger')
          .select('created_at, points, transaction_type')
          .eq('user_id', userId)
          .gte('created_at', startDate.toISOString())
          .order('created_at', { ascending: true }),
      );

      if (!data?.length) {
        return [];
      }

      const pointsByDay = new Map<string, number>();
      data?.forEach((p) => {
        const date = getLocalIsoDate(new Date(p.created_at));
        if (!validDates.has(date)) return;
        const current = pointsByDay.get(date) || 0;
        const amount = p.transaction_type === 'earn' ? p.points : -p.points;
        pointsByDay.set(date, current + amount);
      });

      return dateRange.map((date) => ({
        date,
        points: pointsByDay.get(date) ?? 0,
      }));
    },
    enabled: !!userId,
    staleTime: QUERY_CONFIG.STALE_TIME.LONG, // 5 minutes
    gcTime: QUERY_CONFIG.GC_TIME.MEDIUM, // 10 minutes cache
  });

  const aggregatedPointsTimeline = useMemo(() => pointsData || [], [pointsData]);
  const currentMetricsData = useMemo(
    () => metricsTimeline.slice(-totalDays),
    [metricsTimeline, totalDays],
  );
  const previousMetricsData = useMemo(
    () => metricsTimeline.slice(0, totalDays),
    [metricsTimeline, totalDays],
  );
  const currentPointsData = useMemo(
    () => aggregatedPointsTimeline.slice(-totalDays),
    [aggregatedPointsTimeline, totalDays],
  );
  const previousPointsData = useMemo(
    () => aggregatedPointsTimeline.slice(0, totalDays),
    [aggregatedPointsTimeline, totalDays],
  );
  const metricCoverage = useMemo(
    () => buildMetricCoverage(currentMetricsData),
    [currentMetricsData],
  );
  const previousMetricCoverage = useMemo(
    () => buildMetricCoverage(previousMetricsData),
    [previousMetricsData],
  );

  return useMemo(
    () => ({
      metricsData: currentMetricsData,
      previousMetricsData,
      pointsData: currentPointsData,
      previousPointsData,
      metricCoverage,
      previousMetricCoverage,
      isLoading: metricsLoading || pointsLoading,
      fetchNextPage: undefined,
      hasNextPage: false,
      isFetchingNextPage: false,
    }),
    [
      currentMetricsData,
      previousMetricsData,
      currentPointsData,
      previousPointsData,
      metricCoverage,
      previousMetricCoverage,
      metricsLoading,
      pointsLoading,
    ],
  );
};
